

import React, { useState, useEffect, SyntheticEvent } from 'react';
import styled from 'styled-components';

import {
  InputRowWithTextInput,
  InputRowWithEmailInput,
  InputRowWithTextArea,
} from './InputRow';
import ActionRow from './ActionRow';
import type {
  EmailOptin,
  PatchMessageConfigurationValuesRequest,
} from '../../types/messageConfiguration';
import type { StyledComponentWithChildren } from '../../types/styledComponent';
import { encodeToBase64 } from '../../lib/encodeToBase64';

const FormCard: StyledComponentWithChildren = styled.div`
  display: flex;
  flex-direction: column;
`;
interface EmailOptinProps {
  emailOptin?: EmailOptin;
  domainList: string[];
  emailList: string[];
  onSave: (patchRequest: PatchMessageConfigurationValuesRequest) => void;
}

const EmailOptinForm: React.FC<EmailOptinProps> = ({
  emailOptin,
  domainList,
  emailList,
  onSave,
}) => {
  const [emailInput, updateEmailInput] = useState(emailOptin?.fromEmail || '');
  const [aliasInput, updateAliasInput] = useState(emailOptin?.fromAlias || '');
  const [subjectInput, updateSubjectInput] = useState(emailOptin?.subject || '');
  const [headerInput, updateHeaderInput] = useState(emailOptin?.header || '');
  const [openingInput, updateOpeningInput] = useState(emailOptin?.opening || '');
  const [closingInput, updateClosingInput] = useState(emailOptin?.closing || '');

  const [emailInputMessage, updateEmailInputMessage] = useState<any>();

  // Though this may seem redundant, it's to allow for non-email causes of form error to be added in the below useEffect
  const [hasFormError, updateHasFormError] = useState(false);

  useEffect(() => {
    // if emailInputMessage is falsy or type !== error, set HasFormError false, else true
    updateHasFormError(emailInputMessage?.type === 'error');
  }, [emailInputMessage]);

  const namePrefix = 'emailOptIn';

  const updateInputStateWithProps = (): void => {
    updateEmailInput(emailOptin?.fromEmail || '');
    updateAliasInput(emailOptin?.fromAlias || '');
    updateSubjectInput(emailOptin?.subject || '');
    updateHeaderInput(emailOptin?.header || '');
    updateOpeningInput(emailOptin?.opening || '');
    updateClosingInput(emailOptin?.closing || '');
  };

  const handleOnCancel = (): void => {
    updateInputStateWithProps();
  };

  useEffect(() => {
    updateInputStateWithProps();
  }, [emailOptin]);

  const updateMessageConfigValues = () => {
    const patchRequest: PatchMessageConfigurationValuesRequest = {
      messageConfigurationValues: {
        emailOptin: {
          ...(emailInput && { fromEmail: emailInput }),
          ...(aliasInput && { fromAlias: aliasInput }),
          ...(subjectInput && { subject: subjectInput }),
          ...(headerInput && { header: encodeToBase64(headerInput) }),
          ...(openingInput && { opening: encodeToBase64(openingInput) }),
          ...(closingInput && {
            closing: encodeToBase64(closingInput),
          }),
        },
      },
    };
    onSave(patchRequest);
  };

  return (
    <FormCard>
      <ActionRow
        id={`${namePrefix}Top`}
        saveDisabled={hasFormError}
        onCancel={handleOnCancel}
        onSave={updateMessageConfigValues}
      />

      <InputRowWithEmailInput
        name={`${namePrefix}FromEmail`}
        labelText="From Email Address:"
        inputValue={emailInput}
        onChange={updateEmailInput}
        setDisplayMessage={updateEmailInputMessage}
        displayMessage={emailInputMessage}
        domainList={domainList}
        emailList={emailList}
      />

      <InputRowWithTextInput
        name={`${namePrefix}FromAlias`}
        labelText="From Alias:"
        inputValue={aliasInput}
        onChange={(e: SyntheticEvent<HTMLInputElement>): void => {
          updateAliasInput(e.currentTarget.value);
        }}
      />

      <InputRowWithTextInput
        name={`${namePrefix}Subject`}
        labelText="Subject:"
        inputValue={subjectInput}
        onChange={(e: SyntheticEvent<HTMLInputElement>): void => {
          updateSubjectInput(e.currentTarget.value);
        }}
      />

      <InputRowWithTextArea
        name={`${namePrefix}Header`}
        labelText="Header:"
        inputValue={headerInput}
        onChange={(e: SyntheticEvent<HTMLTextAreaElement>): void => {
          updateHeaderInput(e.currentTarget.value);
        }}
      />

      <InputRowWithTextArea
        name={`${namePrefix}Opening`}
        labelText="Opening:"
        inputValue={openingInput}
        onChange={(e: SyntheticEvent<HTMLTextAreaElement>): void => {
          updateOpeningInput(e.currentTarget.value);
        }}
      />

      <InputRowWithTextArea
        name={`${namePrefix}Closing`}
        labelText="Closing:"
        inputValue={closingInput}
        onChange={(e: SyntheticEvent<HTMLTextAreaElement>): void => {
          updateClosingInput(e.currentTarget.value);
        }}
      />

      <ActionRow
        id={`${namePrefix}Bottom`}
        saveDisabled={hasFormError}
        onCancel={handleOnCancel}
        onSave={updateMessageConfigValues}
      />
    </FormCard>
  );
};

export default EmailOptinForm;
